import React, { useEffect, useRef, useState } from "react";
import { changePassword, me as fetchMe, setAvatar, updateProfile, uploadFile, type MeResponse } from "./api";
import { AuthedImage } from "./AuthedImage";
import { t, type Lang } from "./i18n";

type Props = {
  me: MeResponse | null;
  lang: Lang;
  currencyOptions: string[];
  languageOptions: Array<{ code: string; label: string }>;
  onProfileUpdated: (me: MeResponse) => void;
  onDisplayNameChange: (name: string) => void;
  onNavigate: (path: string) => void;
};

export function ProfilePage({
  me,
  lang,
  currencyOptions,
  languageOptions,
  onProfileUpdated,
  onDisplayNameChange,
  onNavigate,
}: Props) {
  const [displayName, setDisplayName] = useState(me?.display_name ?? "");
  const [preferredLanguage, setPreferredLanguage] = useState(me?.preferred_language || languageOptions[0].code);
  const [preferredCurrency, setPreferredCurrency] = useState(me?.preferred_currency || currencyOptions[0]);
  const [dirtyDisplayName, setDirtyDisplayName] = useState(false);
  const [dirtyLanguage, setDirtyLanguage] = useState(false);
  const [dirtyCurrency, setDirtyCurrency] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [avatarUrl, setAvatarUrl] = useState(me?.avatar_url || "");
  const [avatarFileName, setAvatarFileName] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const avatarInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!dirtyDisplayName) setDisplayName(me?.display_name ?? "");
    if (!dirtyLanguage) setPreferredLanguage(me?.preferred_language || languageOptions[0].code);
    if (!dirtyCurrency) setPreferredCurrency(me?.preferred_currency || currencyOptions[0]);
    setAvatarUrl(me?.avatar_url || "");
  }, [me, currencyOptions, dirtyCurrency, dirtyDisplayName, dirtyLanguage, languageOptions]);

  async function onSaveProfile() {
    setStatus(null);
    try {
      await updateProfile({
        display_name: displayName || null,
        preferred_language: preferredLanguage,
        preferred_currency: preferredCurrency,
      });
      const refreshed = await fetchMe();
      setDirtyDisplayName(false);
      setDirtyLanguage(false);
      setDirtyCurrency(false);
      onProfileUpdated(refreshed);
      onDisplayNameChange(refreshed.display_name || "");
    } catch (e) {
      setStatus(String(e));
    }
  }

  async function onChangePassword() {
    setStatus(null);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
    } catch (e) {
      setStatus(String(e));
    }
  }

  async function onUploadAvatar(file: File) {
    setStatus(null);
    try {
      const upload = await uploadFile(file);
      const resp = await setAvatar(upload.id);
      onProfileUpdated(resp);
      setAvatarUrl(resp.avatar_url || "");
      setAvatarFileName(file.name || "");
    } catch (e) {
      setStatus(String(e));
    }
  }

  const cardStyle: React.CSSProperties = {
    background: "var(--swk-card)",
    borderRadius: 16,
    boxShadow: "0 8px 18px rgba(0,0,0,0.04)",
    padding: 14,
  };
  const labelStyle: React.CSSProperties = { display: "grid", gap: 6 };
  const controlBase: React.CSSProperties = { width: "100%", boxSizing: "border-box", fontSize: 16 };
  const inputStyle: React.CSSProperties = {
    ...controlBase,
    padding: "12px 12px",
    borderRadius: 12,
    border: "1px solid var(--swk-border)",
    background: "var(--swk-input-bg)",
    color: "var(--swk-input-text)",
    minHeight: 44,
  };
  const primaryButton: React.CSSProperties = {
    ...controlBase,
    padding: "12px",
    background: "#0f8c57",
    color: "#fff",
    borderRadius: 12,
    border: "none",
    fontWeight: 700,
    minHeight: 44,
  };
  const secondaryButton: React.CSSProperties = {
    ...controlBase,
    padding: "10px 12px",
    background: "var(--swk-input-bg)",
    color: "#0f8c57",
    borderRadius: 12,
    border: "1px solid var(--swk-green)",
    fontWeight: 700,
    minHeight: 44,
  };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={cardStyle}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>{t(lang, "profileHeading")}</h2>

        <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              overflow: "hidden",
              border: "1px solid #ddd",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#f5f5f5",
              fontWeight: 800,
              fontSize: 22,
              flex: "0 0 auto",
            }}
          >
            {avatarUrl ? (
              <AuthedImage
                src={avatarUrl}
                alt="avatar"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                fallback={(displayName || me?.email || "?").slice(0, 1).toUpperCase()}
              />
            ) : (
              (displayName || me?.email || "?").slice(0, 1).toUpperCase()
            )}
          </div>

          <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            <div style={{ fontSize: 12, color: "#69727d", marginBottom: 4 }}>{t(lang, "email")}</div>
            <div style={{ fontWeight: 700 }}>{me?.email || "(unknown)"}</div>
          </div>

          <div style={{ display: "grid", gap: 6, flex: "1 1 220px", minWidth: 0 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#0f8c57" }}>{t(lang, "avatar")}</label>
            <input
              ref={avatarInputRef}
              style={{ display: "none" }}
              type="file"
              accept="image/*"
              onChange={(e) => {
                const f = e.target.files?.[0];
                setAvatarFileName(f?.name || "");
                if (f) onUploadAvatar(f).catch((err) => setStatus(String(err)));
              }}
            />
            <button type="button" style={secondaryButton} onClick={() => avatarInputRef.current?.click()}>
              {lang === "ru" ? "Загрузить аватар" : "Upload avatar"}
            </button>
            {avatarFileName && <div style={{ fontSize: 12, color: "#69727d" }}>{avatarFileName}</div>}
            {avatarUrl && (
              <button
                type="button"
                style={{ ...secondaryButton, borderColor: "#c65a2b", color: "#c65a2b" }}
                onClick={() => {
                  setAvatar(null)
                    .then((resp) => {
                      onProfileUpdated(resp);
                      setAvatarUrl("");
                      setAvatarFileName("");
                      if (avatarInputRef.current) avatarInputRef.current.value = "";
                    })
                    .catch((err) => setStatus(String(err)));
                }}
              >
                {t(lang, "removeAvatar")}
              </button>
            )}
          </div>
        </div>

        <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
          <label style={labelStyle}>
            <span style={{ fontWeight: 700 }}>{t(lang, "displayName")}</span>
            <input
              className="swk-input"
              style={inputStyle}
              value={displayName}
              onChange={(e) => {
                setDirtyDisplayName(true);
                setDisplayName(e.target.value);
              }}
              placeholder="John Doe"
            />
          </label>

          <div style={labelStyle}>
            <span style={{ fontWeight: 700 }}>{t(lang, "language")}</span>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {languageOptions.map((opt) => (
                <button
                  key={opt.code}
                  type="button"
                  onClick={() => {
                    setDirtyLanguage(true);
                    setPreferredLanguage(opt.code);
                  }}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    minHeight: 44,
                    padding: "8px 12px",
                    border: preferredLanguage === opt.code ? "2px solid #0f8c57" : "1px solid #dde4ec",
                    background: preferredLanguage === opt.code ? "#e8f3ec" : "#fff",
                    borderRadius: 12,
                    cursor: "pointer",
                    fontWeight: 700,
                    color: "#0f8c57",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <label style={labelStyle}>
            <span style={{ fontWeight: 700 }}>{t(lang, "preferredCurrency")}</span>
            <select
              className="swk-select"
              style={{ ...inputStyle, paddingRight: 36 }}
              value={preferredCurrency}
              onChange={(e) => {
                setDirtyCurrency(true);
                setPreferredCurrency(e.target.value);
              }}
            >
              {currencyOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>

          <button type="button" style={primaryButton} onClick={() => onSaveProfile().catch((e) => setStatus(String(e)))}>
            {t(lang, "saveProfile")}
          </button>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ fontSize: 16, fontWeight: 800, color: "#0f8c57", marginBottom: 10 }}>{t(lang, "changePassword")}</div>
        <div style={{ fontSize: 12, color: "#69727d", marginBottom: 10 }}>
          {lang === "ru" ? "Новый пароль: минимум 8 символов." : "New password: minimum 8 characters."}
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          <label style={labelStyle}>
            <span style={{ fontWeight: 700 }}>{t(lang, "currentPassword")}</span>
            <input
              className="swk-input"
              style={inputStyle}
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder={lang === "ru" ? "Текущий пароль" : "Current password"}
            />
          </label>
          <label style={labelStyle}>
            <span style={{ fontWeight: 700 }}>{t(lang, "newPassword")}</span>
            <input
              className="swk-input"
              style={inputStyle}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={lang === "ru" ? "Новый пароль" : "New password"}
            />
          </label>
          <button
            type="button"
            style={{ ...primaryButton, opacity: !currentPassword || !newPassword ? 0.6 : 1 }}
            disabled={!currentPassword || !newPassword}
            onClick={() => onChangePassword().catch((e) => setStatus(String(e)))}
          >
            {t(lang, "changePassword")}
          </button>
        </div>
      </div>

      {status && (
        <pre style={{ margin: 0, whiteSpace: "pre-wrap", color: "#b00", background: "#fff3f3", padding: 12, borderRadius: 12 }}>
          {status}
        </pre>
      )}
    </div>
  );
}
